import { Directive, EventEmitter, HostListener, Output } from '@angular/core';

/**
 * Detects basic swipe gestures and emits corresponding events.
 *
 * Emits when a quick touch movement is detected (less than ~500ms):
 * - `swipeLeft`, `swipeRight` for horizontal moves (>30px)
 * - `swipeUp`, `swipeDown` for vertical moves (>60px)
 *
 * Example:
 * ```html
 * <div swipe
 *      (swipeLeft)="onLeft()"
 *      (swipeRight)="onRight()"
 *      (swipeUp)="onUp()"
 *      (swipeDown)="onDown()"></div>
 * ```
 */
@Directive({
    selector: '[swipe]',
    standalone: true,
})
export class SwipeDirective {
    @Output()
    /**
     * Emitted on quick horizontal swipe to the left.
     */
    public swipeLeft = new EventEmitter();

    @Output()
    /**
     * Emitted on quick horizontal swipe to the right.
     */
    public swipeRight = new EventEmitter();

    @Output()
    /**
     * Emitted on quick vertical swipe down.
     */
    public swipeDown = new EventEmitter();

    @Output()
    /**
     * Emitted on quick vertical swipe up.
     */
    public swipeUp = new EventEmitter();

    /**
     * Touch starting point and time used to calculate swipe direction and speed.
     */
    defaultTouch = { x: 0, y: 0, time: 0 };

    @HostListener('touchstart', ['$event'])
    //@HostListener('touchmove', ['$event'])
    @HostListener('touchend', ['$event'])
    @HostListener('touchcancel', ['$event'])
    /**
     * Handles touch events and computes swipe based on deltas and duration.
     */
    handleTouch(event) {
        let touch = event.touches[0] || event.changedTouches[0];

        // check the events
        if (event.type === 'touchstart') {
            this.defaultTouch.x = touch.pageX;
            this.defaultTouch.y = touch.pageY;
            this.defaultTouch.time = event.timeStamp;
        } else if (event.type === 'touchend') {
            let deltaX = touch.pageX - this.defaultTouch.x;
            let deltaY = touch.pageY - this.defaultTouch.y;
            let deltaTime = event.timeStamp - this.defaultTouch.time;

            // simulte a swipe -> less than 500 ms and more than 60 px
            if (deltaTime < 500) {
                // touch movement lasted less than 500 ms
                if (Math.abs(deltaX) > 30) {
                    // delta x is at least 60 pixels
                    if (deltaX > 0) {
                        this.doSwipeRight(event);
                    } else {
                        this.doSwipeLeft(event);
                    }
                }

                if (Math.abs(deltaY) > 60) {
                    // delta y is at least 60 pixels
                    if (deltaY > 0) {
                        this.doSwipeDown(event);
                    } else {
                        this.doSwipeUp(event);
                    }
                }
            }
        }
    }

    /**
     * Emit left-swipe event.
     */
    doSwipeLeft(event) {
        this.swipeLeft.emit(event);
    }

    /**
     * Emit right-swipe event.
     */
    doSwipeRight(event) {
        this.swipeRight.emit(event);
    }

    /**
     * Emit up-swipe event.
     */
    doSwipeUp(event) {
        this.swipeUp.emit(event);
    }

    /**
     * Emit down-swipe event.
     */
    doSwipeDown(event) {
        this.swipeDown.emit(event);
    }
}
